/**
 * Dialog to pick a questionnaire (built-in or a ready custom template),
 * a delivery channel, and send it to the patient (Plan 2, Task 4).
 *
 * Unified picker: built-in instrument names/descriptions come from the
 * fetched definitions (never i18n); custom template names come from the
 * template's own parsed_instrument (also never i18n — it's the therapist's
 * own content).
 *
 * Channel gating mirrors send_questionnaire.py's validation EXACTLY (loud,
 * never a silent fallback):
 *   - built-in (instrument_id) => "email" is disabled: built-ins are app-only.
 *   - "app" requires a linked, non-revoked client account
 *     (client_status in "invited"/"active") => otherwise disabled with
 *     "invite the client to the app first".
 *   - "email" requires an email address on file => otherwise disabled with
 *     "no email on file".
 * The "attach original file" checkbox only makes sense for a custom
 * template sent by email (built-ins have no uploaded file to attach).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { InstrumentDefinition, SendQuestionnaireParams } from "./types";
import type { QuestionnaireTemplate } from "./useQuestionnaireFiles";

export type SendSelection = { kind: "builtin" | "custom"; id: string };

type Props = {
  open: boolean;
  instruments: InstrumentDefinition[];
  /** Full template list is fine to pass — this dialog filters to `ready` itself. */
  templates: QuestionnaireTemplate[];
  defaultLang: "en" | "hu";
  patientEmail?: string;
  patientClientStatus?: string;
  patientContactLoading?: boolean;
  /** Pre-select a specific item when opened from e.g. the shelf's per-row Send button. */
  initialSelection?: SendSelection | null;
  onSend: (params: SendQuestionnaireParams) => Promise<unknown>;
  onClose: () => void;
};

export const SendQuestionnaireDialog: React.FC<Props> = ({
  open,
  instruments,
  templates,
  defaultLang,
  patientEmail,
  patientClientStatus,
  patientContactLoading,
  initialSelection,
  onSend,
  onClose,
}) => {
  const { t } = useTranslation();

  const readyTemplates = useMemo(
    () => templates.filter((tpl) => tpl.status === "ready" && tpl.parsedInstrument),
    [templates],
  );

  const [selection, setSelection] = useState<SendSelection | null>(initialSelection ?? null);
  const [channel, setChannel] = useState<"app" | "email">("app");
  const [lang, setLang] = useState<"en" | "hu">(defaultLang);
  const [attachOriginal, setAttachOriginal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fresh send flow every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSelection(initialSelection ?? null);
    setChannel("app");
    setAttachOriginal(false);
    setBusy(false);
    setError(null);
  }, [open, initialSelection]);

  const isBuiltin = selection?.kind === "builtin";
  const isCustom = selection?.kind === "custom";
  const selectedTemplate = isCustom
    ? readyTemplates.find((tpl) => tpl.fileId === selection!.id) ?? null
    : null;

  // Custom templates are single-language (spec §1) — the send lang must
  // match the template's own lang or send_questionnaire.py 400s. Built-ins
  // reset to the panel's UI language on (re)selection.
  useEffect(() => {
    if (!open) return;
    if (selectedTemplate?.parsedInstrument) {
      setLang(selectedTemplate.parsedInstrument.lang);
    } else if (isBuiltin) {
      setLang(defaultLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, open]);

  const clientAccountLinked = patientClientStatus === "invited" || patientClientStatus === "active";

  const appDisabledReason = patientContactLoading
    ? t("assessments.sendGateLoading")
    : !clientAccountLinked
    ? t("assessments.sendGateNoClientAccount")
    : null;

  const emailDisabledReason = patientContactLoading
    ? t("assessments.sendGateLoading")
    : isBuiltin
    ? t("assessments.sendGateBuiltinAppOnly")
    : !patientEmail?.trim()
    ? t("assessments.sendGateNoEmail")
    : null;

  // If the picked channel becomes unavailable for the newly selected item,
  // fall back to whichever channel IS available — loud (Send stays gated),
  // never a silent no-op selection.
  useEffect(() => {
    if (channel === "email" && emailDisabledReason && !appDisabledReason) {
      setChannel("app");
    } else if (channel === "app" && appDisabledReason && !emailDisabledReason) {
      setChannel("email");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, appDisabledReason, emailDisabledReason]);

  if (!open) return null;

  const activeReason = channel === "app" ? appDisabledReason : emailDisabledReason;
  const canSend = !!selection && !busy && !activeReason;
  const showAttachOriginal = isCustom && channel === "email";

  const send = async () => {
    if (!canSend || !selection) return;
    setBusy(true);
    setError(null);
    try {
      await onSend({
        instrumentId: isBuiltin ? selection.id : undefined,
        fileId: isCustom ? selection.id : undefined,
        channel,
        lang,
        attachOriginal: showAttachOriginal ? attachOriginal : undefined,
      });
      onClose();
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={dialogStyles.backdrop} role="dialog" aria-modal="true">
      <div style={dialogStyles.panel}>
        <h3 style={{ margin: 0 }}>{t("assessments.sendQuestionnaire")}</h3>

        <div style={dialogStyles.scroll}>
          <div style={dialogStyles.groupTitle}>{t("assessments.sendBuiltinGroup")}</div>
          <div style={dialogStyles.list}>
            {instruments.map((ins) => (
              <button
                key={ins.instrument_id}
                style={dialogStyles.instrument(isBuiltin && selection!.id === ins.instrument_id)}
                onClick={() => setSelection({ kind: "builtin", id: ins.instrument_id })}
              >
                <strong>{ins.name[defaultLang]}</strong>
                <span style={{ display: "block", fontSize: 12, opacity: 0.75 }}>
                  {ins.description[defaultLang]} · {ins.items.length} {t("assessments.items")}
                </span>
              </button>
            ))}
          </div>

          <div style={dialogStyles.groupTitle}>{t("assessments.sendCustomGroup")}</div>
          {readyTemplates.length ? (
            <div style={dialogStyles.list}>
              {readyTemplates.map((tpl) => (
                <button
                  key={tpl.fileId}
                  style={dialogStyles.instrument(isCustom && selection!.id === tpl.fileId)}
                  onClick={() => setSelection({ kind: "custom", id: tpl.fileId })}
                >
                  <strong>{tpl.parsedInstrument!.name}</strong>
                  <span style={{ display: "block", fontSize: 12, opacity: 0.75 }}>
                    {tpl.parsedInstrument!.items.length} {t("assessments.items")}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, opacity: 0.7 }}>{t("assessments.sendNoTemplates")}</div>
          )}
        </div>

        <div style={dialogStyles.channelRow}>
          <div style={dialogStyles.channelLabel}>{t("assessments.channelLabel")}</div>
          <label style={dialogStyles.channelOption}>
            <input
              type="radio"
              name="qsend-channel"
              checked={channel === "app"}
              onChange={() => setChannel("app")}
              disabled={!!appDisabledReason}
            />
            {t("assessments.sendChannelApp")}
          </label>
          {appDisabledReason ? <div style={dialogStyles.gateReason}>{appDisabledReason}</div> : null}

          <label style={dialogStyles.channelOption}>
            <input
              type="radio"
              name="qsend-channel"
              checked={channel === "email"}
              onChange={() => setChannel("email")}
              disabled={!!emailDisabledReason}
            />
            {t("assessments.sendChannelEmail")}
          </label>
          {emailDisabledReason ? <div style={dialogStyles.gateReason}>{emailDisabledReason}</div> : null}
        </div>

        {isBuiltin ? (
          <div style={dialogStyles.langRow}>
            <label>
              <input type="radio" checked={lang === "en"} onChange={() => setLang("en")} /> English
            </label>
            <label>
              <input type="radio" checked={lang === "hu"} onChange={() => setLang("hu")} /> Magyar
            </label>
          </div>
        ) : null}

        {showAttachOriginal ? (
          <label style={dialogStyles.attachRow}>
            <input
              type="checkbox"
              checked={attachOriginal}
              onChange={(e) => setAttachOriginal(e.target.checked)}
            />
            {t("assessments.attachOriginal")}
          </label>
        ) : null}

        {error && <div style={dialogStyles.error}>{error}</div>}
        <div style={dialogStyles.actions}>
          <button onClick={onClose} disabled={busy}>{t("common.cancel")}</button>
          <button onClick={send} disabled={!canSend}>
            {busy ? t("assessments.sending") : t("assessments.send")}
          </button>
        </div>
      </div>
    </div>
  );
};

const dialogStyles = {
  backdrop: {
    position: "fixed", inset: 0, background: "color-mix(in srgb, var(--color-title) 45%, transparent)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1300,
  } as React.CSSProperties,
  panel: {
    background: "var(--color-surface)", borderRadius: 12, padding: 20, width: 460,
    maxWidth: "calc(100vw - 32px)", maxHeight: "calc(100vh - 64px)",
    display: "flex", flexDirection: "column", gap: 12,
  } as React.CSSProperties,
  scroll: { overflowY: "auto", display: "flex", flexDirection: "column", gap: 4, maxHeight: 320 } as React.CSSProperties,
  groupTitle: { fontSize: 12, fontWeight: 800, opacity: 0.7, marginTop: 4 } as React.CSSProperties,
  list: { display: "flex", flexDirection: "column", gap: 8 } as React.CSSProperties,
  instrument: (active: boolean): React.CSSProperties => ({
    textAlign: "left", padding: "10px 12px", borderRadius: 8, cursor: "pointer",
    border: active ? "2px solid var(--color-brand)" : "1px solid var(--color-border)",
    background: active ? "var(--color-brand-ghost)" : "var(--color-surface)",
  }),
  channelRow: { display: "flex", flexDirection: "column", gap: 4 } as React.CSSProperties,
  channelLabel: { fontSize: 12, fontWeight: 800, opacity: 0.7 } as React.CSSProperties,
  channelOption: { display: "flex", alignItems: "center", gap: 8 } as React.CSSProperties,
  gateReason: { fontSize: 12, color: "var(--color-danger)", marginLeft: 24 } as React.CSSProperties,
  langRow: { display: "flex", gap: 16 } as React.CSSProperties,
  attachRow: { display: "flex", gap: 8, alignItems: "center", fontSize: 13 } as React.CSSProperties,
  error: { color: "var(--color-danger)", fontSize: 13, whiteSpace: "pre-wrap" } as React.CSSProperties,
  actions: { display: "flex", justifyContent: "flex-end", gap: 8 } as React.CSSProperties,
};
